import React, { useState } from 'react';
import {
  useAppStateAssetReportListStore,
  useAppStateCurrentAssetReportStore,
  useAppStatePrevAssetReportStore,
} from '../../../../common/store/AppStateStore.zustand';
import AddAssetReportModal, { AddAssetReportModalData } from './AddAssetReportModal';
import { useAssetReportAPI } from '../../api/AssetReport.api';
import EditAssetReportModal, { EditAssetReportModalData } from './EditAssetReportModal';
import { AssetReport } from '../../type/AssetReport.type';

export interface ManageAssetReportModalData {
  isOpen: boolean;
  onClose: () => void;
}

const ManageAssetReportModal = ({ isOpen, onClose }: ManageAssetReportModalData) => {
  const { getAssetReportList, deleteAssetReport } = useAssetReportAPI();
  const { assetReportList, setAssetReportList } = useAppStateAssetReportListStore();
  const { setCurrentAssetReport } = useAppStateCurrentAssetReportStore();
  const { setPrevAssetReport } = useAppStatePrevAssetReportStore();
  const [addAssetReportModalData, setAddAssetReportModalData] = useState<AddAssetReportModalData>();
  const [editAssetReportModalData, setEditAssetReportModalData] = useState<EditAssetReportModalData>();

  const handleCloseClick = () => {
    onClose();
  };

  const handleAddClick = () => {
    setAddAssetReportModalData({
      isOpen: true,
      onClose: async () => {
        const { data } = await getAssetReportList();
        setAssetReportList(data);
        setCurrentAssetReport(data.length > 0 ? data[0] : undefined);
        setPrevAssetReport(data.length > 1 ? data[1] : undefined);
        setAddAssetReportModalData(undefined);
      },
    });
  };

  const handleDeleteClick = async (assetReportId: string) => {
    await deleteAssetReport(assetReportId);
    const { data } = await getAssetReportList();
    setAssetReportList(data);
    setCurrentAssetReport(data.length > 0 ? data[0] : undefined);
    setPrevAssetReport(data.length > 1 ? data[1] : undefined);
  };

  const handleEditClick = async (assetReport: AssetReport) => {
    setEditAssetReportModalData({
      isOpen: true,
      assetReport: assetReport,
      onClose: async () => {
        const { data } = await getAssetReportList();
        setAssetReportList(data);
        setCurrentAssetReport(data.length > 0 ? data[0] : undefined);
        setPrevAssetReport(data.length > 1 ? data[1] : undefined);
        setEditAssetReportModalData(undefined);
      },
    });
  };

  return isOpen ? (
    <div className="fixed h-screen inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">자산 관리</h2>
            <button onClick={() => handleCloseClick()} className="text-gray-400 hover:text-gray-600">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          <div className="mb-6 flex justify-between items-center">
            <p className="text-gray-600">총 {assetReportList.length}개의 자산이 등록되어 있습니다.</p>
            <button
              onClick={() => handleAddClick()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm flex items-center !rounded-button break-keep"
            >
              <i className="fas fa-plus mr-2"></i> 자산 추가하기
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg">
            {assetReportList.map((assetReport) => (
              <div
                key={assetReport.id}
                className="flex items-center justify-between p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-100"
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-500 mr-3">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <span className="font-medium text-gray-800">{assetReport.yearMonth}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    className="text-gray-400 hover:text-blue-500 p-2"
                    onClick={() => handleEditClick(assetReport)}
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    onClick={() => handleDeleteClick(assetReport.id)}
                    className="text-gray-400 hover:text-red-500 p-2"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={() => handleCloseClick()}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm !rounded-button"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
      {addAssetReportModalData && <AddAssetReportModal {...addAssetReportModalData} />}
      {editAssetReportModalData && <EditAssetReportModal {...editAssetReportModalData} />}
    </div>
  ) : (
    <></>
  );
};

export default ManageAssetReportModal;
